import React from 'react'
import Navbar from '../components/sections/Navbar'
import Footer from '../components/sections/Footer'
import { useSEO } from '../hooks/useSEO'

export default function CookiePolicyPage() {
  useSEO({
    title: 'Cookie Policy | AugmentAppz',
    description: 'Understanding how AugmentAppz uses cookies and similar technologies on our platform.',
  })

  return (
    <>
      <Navbar />
      <main className="legal-page">

        <div className="legal-page__hero">
          <div className="container">
            <p className="legal-page__badge">Legal</p>
            <h1 className="legal-page__title">Cookie Policy</h1>
            <p className="legal-page__subtitle">
              Understanding how AugmentAppz uses cookies and similar technologies on our platform.
            </p>
          </div>
        </div>

        <div className="container legal-page__body">

          <section className="legal-section">
            <h2 className="legal-section__heading">Introduction</h2>
            <p className="legal-section__text">
              This Cookie Policy explains how AUGMENTAPPZ PRIVATE LIMITED (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) uses cookies and
              similar technologies on our website and platform. By using our services, you consent to the use of cookies
              as described in this policy.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">What Are Cookies?</h2>
            <p className="legal-section__text">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a
              website. They help websites remember your preferences, login information, and other settings to enhance your
              browsing experience. Cookies can be &ldquo;session&rdquo; cookies (which expire when you close your browser) or
              &ldquo;persistent&rdquo; cookies (which remain on your device for a specified period).
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">Types of Cookies We Use</h2>

            <h3 className="legal-section__subheading">Essential Cookies</h3>
            <p className="legal-section__text">
              These cookies are necessary for the basic functioning of our website and platform. They enable core features
              such as security, network management, and accessibility. Without these cookies, our services may not function
              properly.
            </p>

            <h3 className="legal-section__subheading">Performance Cookies</h3>
            <p className="legal-section__text">
              These cookies collect information about how visitors use our website, such as which pages are visited most
              often and any error messages that occur. This data helps us improve the performance and user experience of
              our platform.
            </p>

            <h3 className="legal-section__subheading">Functionality Cookies</h3>
            <p className="legal-section__text">
              These cookies allow our website to remember choices you make (such as your language preference or region)
              and provide enhanced, personalized features. They may also be used to provide services you have requested.
            </p>

            <h3 className="legal-section__subheading">Analytics Cookies</h3>
            <p className="legal-section__text">
              We use analytics cookies to understand how visitors interact with our website. This information helps us
              analyze web traffic and improve our website to tailor it to user needs. These cookies may track things like
              how long you spend on the site and the pages that you visit.
            </p>

            <h3 className="legal-section__subheading">Marketing Cookies</h3>
            <p className="legal-section__text">
              These cookies are used to track visitors across websites to display ads that are relevant and engaging for
              the individual user. They are also used to limit the number of times you see an advertisement and help
              measure the effectiveness of advertising campaigns.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">Third-Party Cookies</h2>
            <p className="legal-section__text">
              In addition to our own cookies, we may also use various third-party cookies to report usage statistics of
              the website, deliver advertisements, and so on. These cookies are governed by the respective third party&apos;s
              privacy policy.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">How to Manage Cookies</h2>
            <p className="legal-section__text">
              Most web browsers allow you to control cookies through their settings preferences. You can typically find
              these settings in the &ldquo;Options&rdquo; or &ldquo;Preferences&rdquo; menu of your browser. You can set your browser to:
            </p>
            <ul className="legal-list">
              <li>Block all cookies</li>
              <li>Accept only first-party cookies</li>
              <li>Delete cookies when you close your browser</li>
              <li>Receive a notification before a cookie is stored</li>
            </ul>
            <p className="legal-section__text">
              Please note that disabling cookies may affect the functionality of our website and limit your ability to
              use certain features.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">Changes to This Cookie Policy</h2>
            <p className="legal-section__text">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other
              operational, legal, or regulatory reasons. We encourage you to review this page periodically for the
              latest information on our cookie practices.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">Contact Us</h2>
            <p className="legal-section__text">If you have any questions about our use of cookies, please contact us:</p>
            <div className="legal-contact-card">

              {/* Company header */}
              <div className="lcc-company">
                <span className="lcc-company-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V12h6v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className="lcc-company-name">AUGMENTAPPZ PRIVATE LIMITED</span>
              </div>

              {/* Info rows */}
              <ul className="lcc-rows" role="list">

                <li className="lcc-row">
                  <span className="lcc-row-icon" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <div className="lcc-row-body">
                    <span className="lcc-row-label">Email</span>
                    <a href="mailto:support@augmentappz.ai" className="lcc-row-value">support@augmentappz.ai</a>
                  </div>
                </li>

                <li className="lcc-row">
                  <span className="lcc-row-icon" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </span>
                  <div className="lcc-row-body">
                    <span className="lcc-row-label">Address</span>
                    <span className="lcc-row-value">No 163, 27th Main Road, Sector 1, HSR Layout, Bangalore, Karnataka 560102, India</span>
                  </div>
                </li>

              </ul>
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </>
  )
}
